import { BusinessMarker } from 'selectors/availabilitySearchSelectors';
import { Coordinates, core } from '@tock/coretypes';
import { FunctionComponent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { MAP_ID } from 'maps/mapsConfig';
import { Map, useMap } from '@vis.gl/react-google-maps';
import { Theme, css } from '@emotion/react';
import {
  findMarkerForBusiness,
  fitMapToLocations,
  panMapToLocation,
} from 'components/maps/consumerMapUtils';
import { isEqual } from 'es-toolkit/compat';
import { isTouchScreen } from 'utils/deviceUtils';
import { usePrevious } from '@tock/hooks';
import { useSearchHandlerContext } from 'components/search/SearchContext';
import ConsumerLocationDot from 'components/maps/ConsumerLocationDot';
import CurrentLocationControl from 'components/profile/culinary-travel-log/CurrentLocationControl';
import MapsApiProvider from 'maps/MapsApiProvider';
import Refresh from 'components/profile/culinary-travel-log/Refresh';
import ZoomControls from 'components/profile/culinary-travel-log/ZoomControls';

const locationControlStyles = css`
  padding: 1.8rem;
  padding-top: calc(1.8rem + env(safe-area-inset-top));
`;

// Weirdly, replacing this with a literal causes strange map behavior on pin selection
const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: 44.076613, lng: -98.362239833 };

export interface ConsumerMapNewProps {
  children: ReactNode;
  hasControls?: boolean;
  isMapLoaded: boolean;
  latOffsetFactor?: number;
  loadError?: Error;
  locations: google.maps.LatLngLiteral[];
  locationsSortedGeographically?: boolean;
  markers?: BusinessMarker[];
  metroArea?: core.MetroArea;
  onMapClick(): void;
  selectedBusinessId?: number;
  showCurrentLocation?: boolean;
  gestureHandling?: google.maps.MapOptions['gestureHandling'];
  zoomControl?: google.maps.MapOptions['zoomControl'];
  zoom?: google.maps.MapOptions['zoom'];
  onSearch?(): void;
}

/**
 * Inner controller rendered under the `MapsApiProvider`. `useMap()` resolves the
 * default `<Map>` instance registered by APIProvider, letting us port the legacy
 * imperative logic (`fitBounds`, `panTo`, `getBounds`) without a `mapRef`/`onLoad`
 * callback. "Search this area" is revealed by positive user-gesture signals
 * (`dragend`, `dblclick`, and the `ZoomControls` onUserZoom callback), never by
 * inferring the end of a programmatic move.
 */
const ConsumerMapController: FunctionComponent<ConsumerMapNewProps> = ({
  children,
  gestureHandling,
  hasControls = true,
  latOffsetFactor = 0,
  loadError,
  locations,
  locationsSortedGeographically,
  markers,
  metroArea,
  onMapClick,
  selectedBusinessId,
  showCurrentLocation = false,
  zoom,
  zoomControl,
}) => {
  const map = useMap();
  const [initialZoom, setInitialZoom] = useState<number>();
  const [currentLocation, setCurrentLocation] = useState<Coordinates | undefined>();
  const [showRedoSearch, setShowRedoSearch] = useState(false);
  const [loadingMetro, setLoadingMetro] = useState<boolean>(false);
  const isTouch = isTouchScreen();
  const searchHandlerContext = useSearchHandlerContext();
  const updateSearchUrl = searchHandlerContext?.updateSearchUrl;
  const prevLocations = usePrevious(locations);
  const prevMetroArea = usePrevious(metroArea);
  const hasInitialized = useRef(false);
  const hasFitLocations = useRef(false);

  // Run a programmatic camera move (fit/pan/zoom). Programmatic moves never emit
  // the user-gesture events (`dragend`/`dblclick`) that reveal "Search this
  // area", so no suppression flag is needed — they simply hide the button, and
  // nothing can get "stuck". The `.catch()` swallows a rejecting `move()` (e.g.
  // the metro-viewport geocode branch) so it doesn't surface as an unhandled
  // rejection.
  const runMapMove = useCallback(
    (move: () => void | Promise<void>): Promise<void> => {
      if (!map) {
        return Promise.resolve();
      }
      // A programmatic reposition is never a "user searched this area" action.
      setShowRedoSearch(false);
      return Promise.resolve(move()).catch((error) => {
        window.LOGGER?.error(
          'Programmatic map move failed',
          error instanceof Error ? error : new Error(String(error))
        );
      });
    },
    [map]
  );

  // One-time initial fit, once the map instance is available.
  useEffect(() => {
    if (!map || hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;
    runMapMove(() =>
      fitMapToLocations(map, locations, metroArea, locationsSortedGeographically).then(() => {
        if (markers && markers.length > 0 && selectedBusinessId) {
          const newMarkerSelected = findMarkerForBusiness(markers, selectedBusinessId);
          if (newMarkerSelected) {
            panMapToLocation(map, newMarkerSelected.location);
          }
        }
      })
    );
  }, [
    locations,
    locationsSortedGeographically,
    map,
    markers,
    metroArea,
    selectedBusinessId,
    runMapMove,
  ]);

  // Reveal "Search this area" only on positive user-gesture signals. `dragend`
  // fires on a real pan/drag but never on programmatic `panTo`/`fitBounds`, and
  // `dblclick` fires on a user double-click zoom — so unlike a raw
  // `bounds_changed`/`zoom_changed` listener these can't leak on programmatic
  // moves. (User zoom-button clicks are handled via `ZoomControls`' onUserZoom.)
  useEffect(() => {
    if (!map) {
      return;
    }
    const reveal = () => setShowRedoSearch(true);
    const dragEnd = map.addListener('dragend', reveal);
    const dblClick = map.addListener('dblclick', reveal);
    return () => {
      google.maps.event.removeListener(dragEnd);
      google.maps.event.removeListener(dblClick);
    };
  }, [map]);

  // Correct the initial fit for the race where the map instance is available
  // (via `useMap()`) before the search results have loaded: the one-time fit
  // above would have used the metro-area viewport (empty locations) and the
  // legacy `onLoad` fit ran later, after results arrived. Re-fit once, the first
  // time real locations are present, so we match the legacy zoom.
  useEffect(() => {
    if (!map || hasFitLocations.current || !locations || locations.length === 0) {
      return;
    }
    hasFitLocations.current = true;
    runMapMove(() => fitMapToLocations(map, locations, metroArea, locationsSortedGeographically));
  }, [map, locations, metroArea, locationsSortedGeographically, runMapMove]);

  useEffect(() => {
    if (map) {
      const currentZoom = map.getZoom();
      // if the current zoom is initial, and a custom zoom was provided
      if (currentZoom === initialZoom && zoom && zoom !== initialZoom) {
        runMapMove(() => {
          map.setZoom(zoom);
        });
      }
    }
    if (markers && markers.length > 0 && map && selectedBusinessId) {
      const newMarkerSelected = findMarkerForBusiness(markers, selectedBusinessId);
      if (newMarkerSelected) {
        const bounds = map.getBounds();
        // only pan the map if the pin is out of the current view
        if (bounds && !bounds.contains(newMarkerSelected.location)) {
          const neLat = bounds.getNorthEast().lat();
          const swLat = bounds.getSouthWest().lat();
          const offset = (neLat - swLat) * latOffsetFactor;
          runMapMove(() => {
            panMapToLocation(map, {
              ...newMarkerSelected.location,
              lat: newMarkerSelected.location.lat - offset,
            });
          });
        }
      }
    }
  }, [selectedBusinessId, latOffsetFactor, markers, zoom, initialZoom, map, runMapMove]);

  useEffect(() => {
    async function refitMap() {
      if (map) {
        await runMapMove(() =>
          fitMapToLocations(map, locations, metroArea, locationsSortedGeographically)
        );
      }
    }
    if (metroArea !== prevMetroArea) {
      setLoadingMetro(true);
    }
    // only respond to locations changes when we're changing metros
    if (metroArea && loadingMetro && !isEqual(locations, prevLocations)) {
      refitMap().then(() => {
        setLoadingMetro(false);
      });
    }
  }, [
    map,
    metroArea,
    prevMetroArea,
    locations,
    locationsSortedGeographically,
    loadingMetro,
    prevLocations,
    runMapMove,
  ]);

  const onLocation = (location: Coordinates) => {
    setCurrentLocation(location);
  };

  const onRefreshWithBounds = () => {
    const bounds = map?.getBounds();
    if (!bounds || bounds.isEmpty()) {
      return;
    }

    const { lat: topRightLat, lng: topRightLon } = bounds.getNorthEast();
    const { lat: bottomLeftLat, lng: bottomLeftLon } = bounds.getSouthWest();
    updateSearchUrl &&
      updateSearchUrl({
        topLeftLat: topRightLat().toString(),
        topLeftLon: bottomLeftLon().toString(),
        bottomRightLat: bottomLeftLat().toString(),
        bottomRightLon: topRightLon().toString(),
      });
    setShowRedoSearch(false);
  };

  const onZoomChanged = () => {
    if (!map) {
      return;
    }
    const currentZoom = map.getZoom();
    if (!initialZoom) {
      setInitialZoom(currentZoom);
    }
  };

  // Current location control button
  const currentLocationControlContent = map ? (
    <CurrentLocationControl
      css={locationControlStyles}
      currentLocation={currentLocation}
      latOffsetFactor={latOffsetFactor}
      map={map}
      onLocation={onLocation}
    />
  ) : null;

  // search this area button
  const refreshSearch = map && updateSearchUrl ? <Refresh onClick={onRefreshWithBounds} /> : null;

  // Zoom control buttons. A user-initiated zoom is a positive gesture signal, so
  // reveal "Search this area" (programmatic `setZoom` never goes through here).
  const zoomControlsContent = map ? (
    <ZoomControls map={map} onUserZoom={() => setShowRedoSearch(true)} />
  ) : null;

  // We want to show zoom controls whenever we're not using a touch device
  const customControlsContainer = (
    <div
      css={({ breakpoints }: Theme) => css`
        position: absolute;
        top: 0;
        left: 0;

        ${breakpoints.up('sm')} {
          left: initial;
          right: 0;
        }

        & svg {
          font-size: 1.6rem;
        }
      `}
    >
      {hasControls || !isTouch ? zoomControlsContent : null}
      {showCurrentLocation ? currentLocationControlContent : null}
    </div>
  );

  const centerControlsContainer = (
    <div
      css={({ breakpoints }: Theme) => css`
        position: absolute;
        top: 0;
        transform: translate(50%, 0);
        left: initial;
        right: 50%;

        & svg {
          font-size: 1.6rem;
        }
      `}
    >
      {showRedoSearch ? refreshSearch : null}
    </div>
  );

  if (loadError) {
    return <div>{String(loadError)}</div>;
  }

  return (
    <Map
      mapId={MAP_ID}
      style={{ width: '100%', height: '100%' }}
      clickableIcons={false}
      disableDefaultUI={true}
      scrollwheel={false}
      keyboardShortcuts={false}
      fullscreenControl={false}
      gestureHandling={gestureHandling}
      zoomControl={zoomControl}
      defaultZoom={12}
      defaultCenter={DEFAULT_CENTER}
      onClick={() => onMapClick()}
      onZoomChanged={onZoomChanged}
      reuseMaps
    >
      {centerControlsContainer}
      {customControlsContainer}
      {children}
      {currentLocation && (
        <ConsumerLocationDot
          position={{ lat: currentLocation.latitude, lng: currentLocation.longitude }}
        />
      )}
    </Map>
  );
};

/**
 * vis.gl (flag-ON) replacement for `ConsumerMap`. Self-wraps a `MapsApiProvider`
 * so it works whether or not an ancestor already mounted one (vis.gl dedupes the
 * script load across providers), keeping parents unchanged.
 */
const ConsumerMapNew: FunctionComponent<ConsumerMapNewProps> = (props) => (
  <MapsApiProvider>
    <ConsumerMapController {...props} />
  </MapsApiProvider>
);

export default ConsumerMapNew;
